<script>
export default {
  name: 'GoodsList'
}

</script>

<script setup>
import { ref, computed } from 'vue';
import axios from 'axios';
import AddToCartConfirmModal from "./modal/AddToCartConfirmModal.vue";
import AddToCartCompleteModal from "./modal/AddToCartCompleteModal.vue";
import DeleteFromCartConfirmModal from "./modal/DeleteFromCartConfirmModal.vue";
import DeleteFromFavoriteConfirmModal from "./modal/DeleteFromFavoriteConfirmModal.vue";
import IdenticalFormulationModal from "./IdenticalFormulationModal.vue";
import IdenticalFormulationCompleteModal from "./IdenticalFormulationCompleteModal.vue";

import { modalUtil } from '@functions/modalUtil.js';
import Goods from './Goods.vue';
import CheckAllGoods from './CheckAllGoods.vue';
import { url } from '@/config/urls';

// 外部定義メソッド
const { switchFromCurrentModal } = modalUtil();

// emit定義
const emit = defineEmits([
  'updateLoading',
  'updateCheckedList',
  'updateAllCheckedList',
  'countCartGoods',
  'switchToDefaultMode',
  'historyAddToCart',
  'updateGoodsQuantityList',
  'apiError',
  'validationError',
  'resetCartState',
  'cartDeleted',
  'deleteFromFavoriteComplete'
]);

// ローディングフラグ更新
const handleUpdateLoading = (isLoading) => {
  emit('updateLoading', isLoading);
}

// エラー処理
const handleApiError = (error) => {
  emit('apiError', error);
}

const validationErrorHandle = (key, value) => {
　emit('validationError', key, value)
}

// チェック済みリスト更新
const handleUpdateCheckedList = (updatedList) => {
  emit('updateCheckedList', updatedList);
}
//　他ページ込チェック済みリスト更新
const handleUpdateAllCheckedList = (updatedList) => {
  emit('updateAllCheckedList', updatedList);
}
// 商品数量リスト更新
const handleUpdateGoodsQuantityList = (updatedGoodsQuantityList) => {
  emit('updateGoodsQuantityList', updatedGoodsQuantityList);
}

// 同一製剤検索用
const orderedData = ref({});
// 同一製剤モーダル_検索対象データ更新
const updateModalData = (target) => {
  orderedData.value = target;
}
// 同一製剤再発注完了モーダル表示「発注しました」
const openIdenticalFormulationCompleteModal = (target) => {
  switchFromCurrentModal('identicalFormulationCompleteModal');
}

// prop定義
const props = defineProps({
  /**
   * 画面ID
   *    [発注TOP] OrderSearch
   *    [発注履歴] OrderHistory
   *    [お気に入り詳細] OrderFavoriteDetail
   *    [カート] OrderCart
   */
  screenId: {
      type: String,
      required: true
  },
  /**
   * 画面モード
   *     [カート画面] まとめて削除モード：bulkDelete,納品指定日まとめて編集モード：bulkUpdate
   */
  screenMode: {
      type: String,
      required: false,
      default: 'default'
  },
  isPermittedOrder: {
    type: Boolean,
    required: false,
    default: false
  },
  deliveryDateAllowedFlg: {
    type: Number,
    required: false,
    default: 0
  },
  // 商品情報
  goodsList: {
    type: Array,
    required: true
  },
  // 商品分類マスタデータ
  goodsTypes: {
    type: Array,
    required: true
  },
  // お気に入りデータ
  goodsFavorites: {
    type: Array,
    required: false,
    default: []
  },
  // お気に入りID（お気に入り詳細画面用）
  goodsFavId: {
    type: Number,
    required: false
  },
  // 商品数量リスト（お気に入り詳細画面用）
  goodsQuantityList: {
    type: Array,
    required: false,
    default: []
  },
  // カート追加API URL
  orderCartAddApiUrl: {
    type: String,
    default: url.api.orderCart.add
  },
  // カート削除API URL
  orderCartDeleteApiUrl: {
    type: String,
    default: url.api.orderCart.delete
  },
  // お気に入り削除API URL
  orderFavoriteItemDeleteApiUrl: {
    type: String,
    default: url.api.orderFavoriteItem.delete
  },
  // お気に入り詳細画面URL
  orderFavoriteDetailPageUrl: {
    type: String,
    default: url.page.orderFavoriteDetail
  },
  // カート画面URL
  orderCartPageUrl: {
    type: String,
    default: url.page.orderCart
  },
  // チェック済みリスト
  checkedList: {
    type: Array,
    required: false,
    default: []
  },
  // 他ページ込チェック済みリスト
  allCheckedList: {
    type: Array,
    required: false,
    default: []
  }
});

// モーダル用商品データ（グローバル状態として管理）
const operatingGoodsData = ref(null);
// モーダル用商品データ更新
const setOperatingGoodsData = (goods) => {
  operatingGoodsData.value = goods;
}

// カート商品 追加
const addToCart = () => {
  const janCode = operatingGoodsData.value.janCode;
  const quantity = operatingGoodsData.value.quantity;

  const goods = {
    janCode: janCode,
    quantity: quantity
  };
  emit('updateLoading', true);
  axios.post(props.orderCartAddApiUrl, {goodsList: [goods]})
    .then((response) => {
      switchFromCurrentModal('addToCartCompleteModal');
      emit('countCartGoods');
    })
    .catch((error) => {
      emit('apiError', error);
    })
    .finally(() => {
      // 操作中商品データをクリア
      operatingGoodsData.value = null;
      emit('updateLoading', false);
    });
}

// カート商品 削除
const deleteFromCart = () => {
  const janCode = operatingGoodsData.value.janCode;
  emit('updateLoading', true);
  axios.post(props.orderCartDeleteApiUrl, {janCodeList: [janCode]})
    .then((response) => {
      // 削除完了を親コンポーネントに通知
      emit('cartDeleted');
    })
    .catch((error) => {
      emit('apiError', error);
    })
    .finally(() => {
      // 操作中商品データをクリア
      operatingGoodsData.value = null;
      emit('updateLoading', false);
    });
}

// お気に入り商品削除
const deleteFromFavorite = () => {
  const janCode = operatingGoodsData.value.janCode;
  const param = {
    goodsFavId: props.goodsFavId,
    janCodeList: [janCode]
  };
  handleUpdateLoading(true);
  axios.post(props.orderFavoriteItemDeleteApiUrl, param)
    .then((response) => {
      // do nothing
    })
    .catch((error) => {
      emit('apiError', error);
    })
    .finally(() => {
      handleUpdateLoading(false);
      // 操作中商品データをクリア
      operatingGoodsData.value = null;
      switchFromCurrentModal('deleteFromFavoriteCompleteModal');
    });
}

// お気に入りグループ名称
const goodsFavName = computed(() => {
  if (props.goodsFavId && props.goodsFavorites.find(goodsFavorite => goodsFavorite.id === props.goodsFavId)) {
    return props.goodsFavorites.find(goodsFavorite => goodsFavorite.id === props.goodsFavId).name;
  }
  return '';
});

</script>

<template>
  <CheckAllGoods
    v-if="(props.screenId === 'OrderCart' && (props.screenMode === 'bulkDelete' || props.screenMode === 'bulkUpdate')) || (props.screenId === 'OrderHistory')"
    :screenId = props.screenId
    :screenMode = props.screenMode
    :goodsList = props.goodsList
    :checkedList = props.checkedList
    :allCheckedList = props.allCheckedList
    @updateCheckedList="handleUpdateCheckedList"
    @updateAllCheckedList="handleUpdateAllCheckedList"
    @switchToDefaultMode="emit('switchToDefaultMode')"
    @historyAddToCart="emit('historyAddToCart')"
  ></CheckAllGoods>

  <Goods
    v-for="(goods, index) in goodsList"
    :key="props.screenId === 'OrderHistory' ? goods.orderInfoId : goods.janCode"
    :screenId = props.screenId
    :screenMode = props.screenMode
    :isPermittedOrder = props.isPermittedOrder
    :deliveryDateAllowedFlg = props.deliveryDateAllowedFlg
    :goods = goods
    :goodsTypes = props.goodsTypes
    :goodsFavorites = props.goodsFavorites
    :index = index
    :checkedList = props.checkedList
    :allCheckedList = props.allCheckedList
    :goodsQuantityList = props.goodsQuantityList
    @setOperatingGoodsData="setOperatingGoodsData"
    @updateLoading="handleUpdateLoading"
    @updateCheckedList="handleUpdateCheckedList"
    @updateAllCheckedList="handleUpdateAllCheckedList"
    @addToCart="addToCart"
    @deleteFromCart="deleteFromCart"
    @deleteFromFavorite="deleteFromFavorite"
    @countCartGoods="emit('countCartGoods')"
    @updateGoodsQuantityList="handleUpdateGoodsQuantityList"
    @updateModalData="updateModalData"
    @apiError="handleApiError"
    @validationError="validationErrorHandle"
    @resetCartState="emit('resetCartState')"
  ></Goods>

  <!-- 同一製剤注文モーダル -->
  <IdenticalFormulationModal
    :ordered="orderedData"
    @showFlashMessage="openIdenticalFormulationCompleteModal"
  ></IdenticalFormulationModal>

  <!-- 同一製剤再発注完了モーダル -->
  <IdenticalFormulationCompleteModal></IdenticalFormulationCompleteModal>

  <!-- カートに追加確認モーダル -->
  <AddToCartConfirmModal
    @addToCart="addToCart"
  ></AddToCartConfirmModal>

  <!-- カートに追加完了モーダル -->
  <AddToCartCompleteModal
    :screenId="props.screenId"
  ></AddToCartCompleteModal>
    
  <!-- お気に入り商品 削除確認モーダル -->
  <DeleteFromFavoriteConfirmModal
    :goodsFavName=goodsFavName
    :operatingGoodsData=operatingGoodsData
    @deleteFromFavorite="deleteFromFavorite"
  ></DeleteFromFavoriteConfirmModal>

  <!-- カート商品 削除確認モーダル -->
  <DeleteFromCartConfirmModal
    :operatingGoodsData=operatingGoodsData
      @deleteFromCart="deleteFromCart"
  ></DeleteFromCartConfirmModal>

</template>

